import React, { useState, useEffect, useRef } from "react";
import Checkbox from "@mui/material/Checkbox";
import { RouteComponentProps } from "@gatsbyjs/reach-router";
import {
    APColumn,
    APText,
    APPalette,
    APForm,
    APFormFieldText,
    APFormFieldDropdown,
    APFormFieldCheckbox,
    APFormSubmit,
    useAPForm,
    getIt,
    handleErrorMessage,
    launchDialog,
    showSnackbar,
    REGEX,
    Deferred,
} from "ap-components";
import { useWindowDimensions } from "ap-components/src/hooks/useWindowDimension";
import { AdvisorInfo } from "../WebStory/Models/WebStoryModel";
import PartnerApiServices from "../../Services/PartnerApiServices";
import { ClientOnboardingForm } from "./models";
import AccountVerificationDialog from "./AccountVerificationDialog";
import { OnboardingCard, AdvisorInfoSection, SuccessView } from "./OnboardingChrome";

// --- Constants ---

const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
const OBJECTID_REGEX = /^[a-f\d]{24}$/i;
const MOBILE_BREAKPOINT = 640;
const DEBOUNCE_MS = 500;

const TC_LINK_STYLE: React.CSSProperties = {
    color: APPalette["brand-400"],
    textDecoration: "underline",
    fontWeight: 500,
};

const CLIENT_TYPE_OPTIONS = [
    { value: "individual", label: "Individual" },
    { value: "minor",      label: "Minor" },
    { value: "nre",        label: "NRE" },
    { value: "nro",        label: "NRO" },
];

// --- Helper: parse URL search params ---

function getSearchParam(key: string): string | null {
    const params = new URLSearchParams(window.location.search);
    return params.get(key);
}

// --- MUI Dialog centering fix for mobile ---
// MUI Dialog with scroll="body" uses vertical-align trick that fails on mobile.
// Override with flexbox centering.
const DIALOG_CENTER_CSS = `
.MuiDialog-container.MuiDialog-scrollBody {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
}
.MuiDialog-container.MuiDialog-scrollBody > .MuiPaper-root {
    margin: 0;
    max-width: none;
    background: transparent;
    box-shadow: none;
    border-radius: 0;
    overflow: visible;
}
`;

// --- Main Component ---

export default function ClientOnboardingPage(_props: RouteComponentProps) {
    const rawAdvisor = getSearchParam("advisor");
    const rawReferrer = getSearchParam("referrerId");
    const advisorId = rawAdvisor && OBJECTID_REGEX.test(rawAdvisor) ? rawAdvisor : null;
    const referrerId = rawReferrer && OBJECTID_REGEX.test(rawReferrer) ? rawReferrer : null;
    const productSource = getSearchParam("productSource");

    const [advisorData, setAdvisorData] = useState<AdvisorInfo | null>(null);
    const [loading, setLoading] = useState(true);
    const [downloadLink, setDownloadLink] = useState<string | null>(null);
    const [signedUpClientType, setSignedUpClientType] = useState<string>("individual");

    const { width } = useWindowDimensions();
    const isMobile = (width ?? 1024) < MOBILE_BREAKPOINT;

    useEffect(() => {
        loadAdvisorInfo();
    }, []);

    async function loadAdvisorInfo() {
        const id = advisorId ?? referrerId;
        if (!id) {
            setLoading(false);
            return;
        }
        try {
            const info = await getIt(PartnerApiServices).getClientOnboardingAdvisorInfo(id);
            setAdvisorData(info);
        } catch (error) {
            handleErrorMessage(error);
        } finally {
            setLoading(false);
        }
    }

    const hasLogo = !!advisorData?.image_logo;
    const isReferrerOnly = !advisorId && !!referrerId;

    let content: React.ReactNode;

    if (!loading && !advisorId && !referrerId) {
        content = (
            <APColumn mainAxisAlignment="center" crossAxisAlignment="center" mainAxisSize="max" style={{ width: "100%" }}>
                <APText variant="h3" color={APPalette["negative-300"]}>
                    URL Link is not proper
                </APText>
            </APColumn>
        );
    } else if (loading) {
        content = (
            <APColumn mainAxisAlignment="center" crossAxisAlignment="center" mainAxisSize="max" style={{ width: "100%" }}>
                <APText variant="paragraph-large" color={APPalette["grey-500"]}>Loading...</APText>
            </APColumn>
        );
    } else if (downloadLink) {
        content = (
            <SuccessView
                advisorData={advisorData}
                hasLogo={hasLogo}
                isReferrerOnly={isReferrerOnly}
                downloadLink={downloadLink}
                isMobile={isMobile}
                clientType={signedUpClientType}
            />
        );
    } else {
        content = (
            <FormView
                advisorId={advisorId}
                advisorData={advisorData}
                hasLogo={hasLogo}
                isReferrerOnly={isReferrerOnly}
                isMobile={isMobile}
                productSource={productSource}
                referrerId={referrerId}
                onSuccess={(link, ct) => { setDownloadLink(link); setSignedUpClientType(ct); }}
            />
        );
    }

    return (
        <>
            <style>{DIALOG_CENTER_CSS}</style>
            {content}
        </>
    );
}

// =============================================================================
// Form View
// =============================================================================

function FormView({
    advisorId,
    advisorData,
    hasLogo,
    isReferrerOnly,
    isMobile,
    productSource,
    referrerId,
    onSuccess,
}: {
    advisorId: string | null;
    advisorData: AdvisorInfo | null;
    hasLogo: boolean;
    isReferrerOnly: boolean;
    isMobile: boolean;
    productSource: string | null;
    referrerId: string | null;
    onSuccess: (link: string, clientType: string) => void;
}) {
    const control = useAPForm();
    const formRef = useRef<ClientOnboardingForm>({
        panNumber: "",
        phoneNumber: "",
        email: "",
        clientType: "individual",
        firstName: "",
        guardianPan: "",
    });

    const [clientType, setClientType] = useState<"individual" | "minor" | "nre" | "nro">("individual");
    const [useNreEmail, setUseNreEmail] = useState(false);
    const [formKey, setFormKey] = useState(0);

    const [panValue, setPanValue] = useState("");
    const [phoneValue, setPhoneValue] = useState("");
    const [emailValue, setEmailValue] = useState("");
    const [firstNameValue, setFirstNameValue] = useState("");
    const [guardianPanValue, setGuardianPanValue] = useState("");
    const [panTouched, setPanTouched] = useState(false);
    const [phoneTouched, setPhoneTouched] = useState(false);
    const [emailTouched, setEmailTouched] = useState(false);
    const [phoneServerError, setPhoneServerError] = useState<string | null>(null);
    const [checkingPhone, setCheckingPhone] = useState(false);
    const [emailServerError, setEmailServerError] = useState<string | null>(null);
    const [checkingEmail, setCheckingEmail] = useState(false);
    const [guardianPanTouched, setGuardianPanTouched] = useState(false);
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [proceedCooldownEnd, setProceedCooldownEnd] = useState<number | null>(null);
    const [proceedCooldownRemaining, setProceedCooldownRemaining] = useState(0);
    const lastOtpSentAtRef = useRef<number | null>(null);

    // Debounced phone availability check
    useEffect(() => {
        if (!REGEX.PHONE.test(phoneValue)) {
            setPhoneServerError(null);
            return;
        }
        setCheckingPhone(true);
        const abortController = new AbortController();
        const timer = setTimeout(async () => {
            try {
                await getIt(PartnerApiServices).checkAvailability({ phoneNumber: phoneValue });
                if (!abortController.signal.aborted) setPhoneServerError(null);
            } catch (e: any) {
                if (!abortController.signal.aborted) {
                    setPhoneServerError(e?.response?.status === 400
                        ? "Phone number is linked to an existing account."
                        : null
                    );
                }
            } finally {
                if (!abortController.signal.aborted) setCheckingPhone(false);
            }
        }, DEBOUNCE_MS);
        return () => { clearTimeout(timer); abortController.abort(); setCheckingPhone(false); };
    }, [phoneValue]);

    // Debounced email availability check
    useEffect(() => {
        if (!REGEX.EMAIL.test(emailValue)) {
            setEmailServerError(null);
            return;
        }
        setCheckingEmail(true);
        const abortController = new AbortController();
        const timer = setTimeout(async () => {
            try {
                await getIt(PartnerApiServices).checkAvailability({ email: emailValue });
                if (!abortController.signal.aborted) setEmailServerError(null);
            } catch (e: any) {
                if (!abortController.signal.aborted) {
                    setEmailServerError(e?.response?.status === 400
                        ? "Account already exists with this email"
                        : null
                    );
                }
            } finally {
                if (!abortController.signal.aborted) setCheckingEmail(false);
            }
        }, DEBOUNCE_MS);
        return () => { clearTimeout(timer); abortController.abort(); setCheckingEmail(false); };
    }, [emailValue]);


    // Countdown tick for rate-limit cooldown on Proceed button
    useEffect(() => {
        if (!proceedCooldownEnd) return;
        const tick = () => {
            const remaining = proceedCooldownEnd - Date.now();
            if (remaining <= 0) { setProceedCooldownEnd(null); setProceedCooldownRemaining(0); }
            else setProceedCooldownRemaining(remaining);
        };
        tick();
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, [proceedCooldownEnd]);

    // Derived state
    const isMinor = clientType === "minor";
    const isPhoneMode = (clientType !== "nre" && clientType !== "nro") || !useNreEmail;

    // Server errors always show (the user has no other way to know the value was rejected).
    // Format errors only show after blur (don't yell at the user mid-typing).
    const panDisplayError = panTouched && panValue && !PAN_REGEX.test(panValue)
        ? "Invalid PAN. Sample Format: ABCDE1234F" : null;

    const phoneDisplayError = phoneServerError
        ?? (phoneTouched && phoneValue && !REGEX.PHONE.test(phoneValue)
            ? "Invalid Phone Number. Sample Format: 9876543210" : null);

    const emailDisplayError = emailServerError
        ?? (emailTouched && emailValue && !REGEX.EMAIL.test(emailValue)
            ? "Invalid email address" : null);

    const guardianPanDisplayError = guardianPanTouched && guardianPanValue && !PAN_REGEX.test(guardianPanValue)
        ? "Invalid PAN. Sample Format: ABCDE1234F" : null;

    const isProceedEnabled = termsAccepted && (isMinor
        ? firstNameValue.trim().length > 0
            && PAN_REGEX.test(guardianPanValue)
            && REGEX.PHONE.test(phoneValue) && !phoneServerError && !checkingPhone
        : PAN_REGEX.test(panValue)
            && (isPhoneMode
                ? REGEX.PHONE.test(phoneValue) && !phoneServerError && !checkingPhone
                : REGEX.EMAIL.test(emailValue) && !emailServerError && !checkingEmail));

    function handleClientTypeChange(v: string | null) {
        const type = (v ?? "individual") as "individual" | "minor" | "nre" | "nro";
        setClientType(type);
        formRef.current.clientType = type;
        if (type !== "nre" && type !== "nro") setUseNreEmail(false);
        // Clear all field values and reset derived state
        formRef.current.panNumber = "";
        formRef.current.firstName = "";
        formRef.current.guardianPan = "";
        formRef.current.phoneNumber = "";
        formRef.current.email = "";
        setPanValue("");
        setFirstNameValue("");
        setGuardianPanValue("");
        setPhoneValue("");
        setEmailValue("");
        setPanTouched(false);
        setGuardianPanTouched(false);
        setPhoneTouched(false);
        setEmailTouched(false);
        setPhoneServerError(null);
        setEmailServerError(null);
        // Increment key to force all input fields to remount and display empty
        setFormKey(k => k + 1);
    }

    function handleNreCheckboxToggle(v: boolean) {
        setUseNreEmail(v);
        formRef.current.phoneNumber = "";
        formRef.current.email = "";
        setPhoneValue("");
        setEmailValue("");
        setPhoneServerError(null);
        setEmailServerError(null);
        setPhoneTouched(false);
        setEmailTouched(false);
    }

    async function handleProceed() {
        if (proceedCooldownRemaining > 0) return;
        if (!(await control.validate())) return;
        const form = formRef.current;
        const isNreEmail = (clientType === "nre" || clientType === "nro") && useNreEmail;

        try {
            await getIt(PartnerApiServices).generateSignupOTP(
                isMinor
                    ? { phoneNumber: form.phoneNumber, guardianPan: form.guardianPan, clientType }
                    : isNreEmail
                        ? { email: form.email, panNumber: form.panNumber, clientType }
                        : { phoneNumber: form.phoneNumber, panNumber: form.panNumber, clientType }
            );
            lastOtpSentAtRef.current = Date.now();

            const link = await launchDialog<string>((deferred: Deferred<string>) => (
                <AccountVerificationDialog
                    deferred={deferred}
                    phoneNumber={isNreEmail ? "" : form.phoneNumber}
                    panNumber={isMinor ? undefined : form.panNumber}
                    guardianPan={isMinor ? form.guardianPan : undefined}
                    firstName={isMinor ? form.firstName : undefined}
                    email={isNreEmail ? form.email : undefined}
                    clientType={clientType}
                    subAdvisorId={isReferrerOnly ? undefined : (advisorId ?? undefined)}
                    refId={isReferrerOnly ? (referrerId ?? undefined) : undefined}
                    productSource={productSource ?? undefined}
                    onOtpResent={() => { lastOtpSentAtRef.current = Date.now(); }}
                />
            ));

            onSuccess(link, clientType);
        } catch (error: unknown) {
            const msg = error instanceof Error ? error.message : "";
            if (!msg || msg === "USER_CANCELLED") return;
            // Only set up a countdown when the backend gave us a concrete duration in seconds
            // (e.g. "Please wait 30 seconds..." or "Too many OTP requests. Please wait 60 seconds...").
            // For lockout-style errors with no duration ("Too many incorrect attempts. Please try again in some time"),
            // show the backend message verbatim and don't fabricate a 30s countdown.
            const waitMatch = msg.match(/(\d+)\s*second/i);
            if (waitMatch) {
                const waitMs = parseInt(waitMatch[1], 10) * 1000;
                const refEnd = lastOtpSentAtRef.current ? lastOtpSentAtRef.current + waitMs : 0;
                const cooldownEnd = refEnd > Date.now() ? refEnd : Date.now() + waitMs;
                setProceedCooldownEnd(prev => prev ?? cooldownEnd);
                const remainingSeconds = Math.ceil((cooldownEnd - Date.now()) / 1000);
                showSnackbar(`Please wait ${remainingSeconds} seconds before requesting another OTP`, "negative");
            } else {
                showSnackbar(msg, "negative");
            }
        }
    }

    return (
        <OnboardingCard advisorData={advisorData} hasLogo={hasLogo} isMobile={isMobile}>
            <APForm control={control} style={{ width: "100%" }}>
                <APColumn
                    crossAxisAlignment="center"
                    gap="24px"
                    style={{ width: "100%" }}
                >
                    {/* Advisor info */}
                    {advisorData && !isReferrerOnly && (
                        <AdvisorInfoSection advisorData={advisorData} isMobile={isMobile} />
                    )}

                    {/* Form fields */}
                    <APColumn crossAxisAlignment="stretch" gap="12px" style={{ width: "100%" }}>

                        {/* Client Type dropdown */}
                        <APFormFieldDropdown
                            label="Client Type"
                            required
                            items={CLIENT_TYPE_OPTIONS}
                            initialValue="individual"
                            style={{ margin: 0, width: "100%" }}
                            onChanged={handleClientTypeChange}
                            hideCloseButton
                        />

                        <React.Fragment key={formKey}>

                        {/* PAN field — individual/NRE/NRO */}
                        {!isMinor && (
                            <div onBlur={() => setPanTouched(true)}>
                                <APFormFieldText
                                    label="PAN"
                                    required
                                    maxLength={10}
                                    style={{ margin: 0, width: "100%" }}
                                    initialValue={formRef.current.panNumber}
                                    onChanged={(v) => { formRef.current.panNumber = v.toUpperCase(); setPanValue(v.toUpperCase()); }}
                                    validator={(v) => {
                                        if (!v) return "PAN is required";
                                        if (!PAN_REGEX.test(v.toUpperCase())) return "Invalid PAN. Sample Format: ABCDE1234F";
                                        return null;
                                    }}
                                    error={panDisplayError}
                                    helperText={<span style={{ color: "#9E9E9E", fontFamily: "Inter", fontSize: "12px", fontStyle: "normal", fontWeight: 400, lineHeight: "16px" }}>Sample Format: ABCDE1234F</span>}
                                />
                            </div>
                        )}

                        {/* Minor fields: Minor's Name + Guardian PAN */}
                        {isMinor && (
                            <>
                                <APFormFieldText
                                    label="Minor's Name"
                                    required
                                    style={{ margin: 0, width: "100%" }}
                                    initialValue={formRef.current.firstName}
                                    onChanged={(v) => { formRef.current.firstName = v; setFirstNameValue(v); }}
                                    validator={(v) => {
                                        if (!v || !v.trim()) return "Minor's name is required";
                                        return null;
                                    }}
                                    error={null}
                                />
                                <div onBlur={() => setGuardianPanTouched(true)}>
                                <APFormFieldText
                                    label="Guardian PAN"
                                    required
                                    maxLength={10}
                                    style={{ margin: 0, width: "100%" }}
                                    initialValue={formRef.current.guardianPan}
                                    onChanged={(v) => { formRef.current.guardianPan = v.toUpperCase(); setGuardianPanValue(v.toUpperCase()); }}
                                    validator={(v) => {
                                        if (!v) return "Guardian PAN is required";
                                        if (!PAN_REGEX.test(v.toUpperCase())) return "Invalid PAN. Sample Format: ABCDE1234F";
                                        return null;
                                    }}
                                    error={guardianPanDisplayError}
                                    helperText={<span style={{ color: "#9E9E9E", fontFamily: "Inter", fontSize: "12px", fontStyle: "normal", fontWeight: 400, lineHeight: "16px" }}>Sample Format: ABCDE1234F</span>}
                                />
                                </div>
                            </>
                        )}

                        {/* NRE checkbox — above phone field */}
                        {(clientType === "nre" || clientType === "nro") && (
                            <APFormFieldCheckbox
                                label="I don't have an Indian Phone number"
                                initialValue={useNreEmail}
                                onChanged={handleNreCheckboxToggle}
                                style={{ padding: "0px", marginTop: "-8px", marginBottom: "-8px", marginLeft: "-4px", alignSelf: "flex-start" }}
                            />
                        )}

                        {/* Phone number — individual, minor, and NRE when checkbox unchecked */}
                        {isPhoneMode && (
                            <div onBlur={() => setPhoneTouched(true)}>
                                <APFormFieldText
                                    label="Phone number"
                                    required
                                    maxLength={10}
                                    keyboardType="number"
                                    style={{ margin: 0, width: "100%" }}
                                    initialValue={formRef.current.phoneNumber}
                                    onChanged={(v) => { formRef.current.phoneNumber = v; setPhoneValue(v); }}
                                    validator={(v) => {
                                        if (!v) return "Phone number is required";
                                        if (!REGEX.PHONE.test(v)) return "Invalid Phone Number. Sample Format: 9876543210";
                                        return null;
                                    }}
                                    error={phoneDisplayError}
                                />
                            </div>
                        )}

                        {/* Email — NRE only when checkbox checked */}
                        {(clientType === "nre" || clientType === "nro") && useNreEmail && (
                            <div onBlur={() => setEmailTouched(true)}>
                                <APFormFieldText
                                    label="Email ID"
                                    required
                                    keyboardType="text"
                                    style={{ margin: 0, width: "100%" }}
                                    initialValue={formRef.current.email}
                                    onChanged={(v) => { formRef.current.email = v; setEmailValue(v); }}
                                    validator={(v) => {
                                        if (!v) return "Email is required";
                                        if (!REGEX.EMAIL.test(v)) return "Enter a valid email";
                                        return null;
                                    }}
                                    error={emailDisplayError}
                                />
                            </div>
                        )}

                        </React.Fragment>

                        {/* T&C checkbox */}
                        <div
                            style={{ display: "flex", alignItems: "flex-start", width: "100%", cursor: "pointer" }}
                            onClick={() => setTermsAccepted(v => !v)}
                        >
                            <Checkbox
                                checked={termsAccepted}
                                color="primary"
                                style={{ padding: "2px", marginRight: "8px", flexShrink: 0 }}
                            />
                            <APText variant="paragraph-xsmall" color={APPalette["grey-800"]} style={{ marginTop: "6px" }}>
                                By checking this box, you agree to our{" "}
                                <a
                                    href="https://www.assetplus.in/#!/info/tnc"
                                    style={TC_LINK_STYLE}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    Terms &amp; Conditions
                                </a>{" "}
                                and{" "}
                                <a
                                    href="https://www.assetplus.in/#!/info/privacy"
                                    style={TC_LINK_STYLE}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    Privacy Policy
                                </a>
                            </APText>
                        </div>

                        {/* Proceed button */}
                        <div style={{ width: "100%", paddingTop: "12px" }}>
                            <APFormSubmit
                                type="primary"
                                size="large"
                                style={{
                                    borderRadius: "8px",
                                    backgroundColor: APPalette["brand-300"],
                                    width: "100%",
                                    marginTop: 0,
                                }}
                                onClick={(isProceedEnabled && proceedCooldownRemaining <= 0) ? async () => handleProceed() : undefined}
                            >
                                {proceedCooldownRemaining > 0
                                    ? `Wait ${Math.ceil(proceedCooldownRemaining / 1000)}s`
                                    : "Proceed"}
                            </APFormSubmit>
                        </div>
                    </APColumn>
                </APColumn>
            </APForm>
        </OnboardingCard>
    );
}

